/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Announced } from '@fluentui/react/lib/Announced';
import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { ContextualMenu, DirectionalHint, IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';
import {
    CheckboxVisibility,
    ColumnActionsMode,
    DetailsListLayoutMode,
    DetailsRow,
    IDetailsList,
    IDetailsListProps,
    IDetailsListStyles,
    IDetailsRowProps
} from '@fluentui/react/lib/DetailsList';
import Dialog, { DialogFooter, DialogType } from '@fluentui/react/lib/Dialog';
import { ShimmeredDetailsList } from '@fluentui/react/lib/ShimmeredDetailsList';
import { ITextProps, Text } from '@fluentui/react/lib/Text';
import { ITextField, TextField } from '@fluentui/react/lib/TextField';
import { SelectionMode, classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import { Property } from 'csstype';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { SearchResultsAnnouncer } from 'src/core/components/SearchResultsAnnouncer';
import { TableColumnProps, TableProps } from 'src/core/components/TableProps';
import { useAnnounced } from 'src/core/hooks/useAnnounced';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useZoomLevel } from 'src/core/hooks/useZoomLevel';
import { getStylist, important } from 'src/core/themes/luisStylist';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';

const messages = defineMessages({
    sortAscending: { id: 'core.table.sortAsc', defaultMessage: 'Sort (ascending)' },
    sortDescending: { id: 'core.table.sortDesc', defaultMessage: 'Sort (descending)' },
    resize: { id: 'core.table.resize', defaultMessage: 'Resize' },
    cancel: { id: 'core.table.cancel', defaultMessage: 'Cancel' },
    noSearchResults: { id: 'core.table.noSearchResults', defaultMessage: 'No results found.' },
    emptyPlaceholder: { id: 'core.table.emptyPlaceholder', defaultMessage: 'No items found.' },
    checkButtonAriaLabel: { id: 'core.table.checkButtonAriaLabel', defaultMessage: 'Select row' },
    columnWidth: { id: 'core.table.columnWidth', defaultMessage: 'Enter desired column width pixels:' },
    narratorSelectedItems: { id: 'core.table.narratorSelectedItems', defaultMessage: 'item(s) selected' },
    selectColumnAriaLabel: { id: 'core.table.selectColumnAriaLabel', defaultMessage: 'Toggle selection' },
    resizeColumnDialogTitle: { id: 'core.table.resizeColumnDialogTitle', defaultMessage: 'Resize column' },
    selectAllAriaLabel: { id: 'core.table.selectAllAriaLabel', defaultMessage: 'Toggle selection for all items' },
    sortAscendingAriaLabel: { id: 'core.table.sortAscendingAriaLabel', defaultMessage: 'Column is sorted in ascending order' },
    sortDescendingAriaLabel: { id: 'core.table.sortDescendingAriaLabel', defaultMessage: 'Column is sorted in descending order' }
});

const { getClassName, styleComponent, styleDiv } = getStylist('Table');

const TableContainer = styleDiv('TableContainer', { overflow: 'auto' });

const centralizedCheckbox = getClassName('checkbox', { display: 'flex', justifyContent: 'center', alignItems: 'center' });

const EmptyPlaceholder = styleComponent<ITextProps>(Text)('EmptyPlaceholder', theme =>
    important({
        marginTop: theme.emptyPlaceholderMargin,
        textAlign: 'center',
        fontWeight: theme.emptyPlaceholderFontWeight as Property.FontWeight
    })
);

const listStyles = classNamesFunction<IDetailsListProps, IDetailsListStyles>()({ root: { height: '100%' } });

const defaultOnRenderRow = (props: IDetailsRowProps, selectionPredicate?: (item: IDetailsRowProps['item']) => boolean) => (
    <DetailsRow
        {...props}
        onRenderDetailsCheckbox={(cbProps, cbDefaultRenderer) =>
            cbDefaultRenderer({ ...cbProps, checked: selectionPredicate?.(props.item) ?? cbProps.checked })
        }
        styles={{ cell: { display: 'inline-flex', alignItems: 'center' } }}
    />
);

/**
 * A convenient wrapper around Fabric's table component that provides
 * default styling and accessibility labels.
 */
export const Table = <T extends object>(props: TableProps<T>) => {
    const { localize } = useLocalization();
    const {
        groups,
        selection,
        items = [],
        onRenderRow,
        columns = [],
        onSortColumn,
        onDeleteKeyUp,
        announcedMessage,
        isLoading = false,
        selectionPredicate,
        overallItemsCount = 0,
        areItemsFiltered = false,
        respectViewportHeight = true,
        selectionMode = SelectionMode.multiple,
        checkboxVisibility = CheckboxVisibility.always,
        emptyPlaceholder = localize(messages.emptyPlaceholder),
        ...rest
    } = props;
    const { isAccessibleMode } = useZoomLevel();
    const { announcedComponent: sortAnnouncer, announceAction } = useAnnounced();

    const detailsListRef = React.useRef<IDetailsList>(null);
    const resizeDialogTextfieldRef = React.useRef<ITextField>(null);
    const columnToEditRef = React.useRef<TableColumnProps | null>(null);

    const [tableKey, setTableKey] = React.useState(getId());
    const [isResizeDialogHidden, setIsResizeDialogHidden] = React.useState(true);
    const [contextualMenuProps, setContextualMenuProps] = React.useState<IContextualMenuProps | undefined>(undefined);

    const handleColumnClick = React.useCallback(
        (
            ev: React.MouseEvent<HTMLElement>,
            column: TableColumnProps,
            defaultColumnClickCb?: (ev: React.MouseEvent<HTMLElement>, column: TableColumnProps) => void
        ): void => {
            defaultColumnClickCb?.(ev, column);
            setContextualMenuProps(c => (c ? undefined : getContextualMenuProps(ev, { ...column, onColumnClick: defaultColumnClickCb })));
        },
        [contextualMenuProps]
    );

    const adjustedColumns = React.useMemo(
        () =>
            columns.map((col: TableColumnProps) => {
                const hasDropdown = col.isResizable || !!onSortColumn;
                return {
                    isSortable: !!onSortColumn,
                    ...col,
                    onColumnClick: (ev: React.MouseEvent<HTMLElement>, column: TableColumnProps) =>
                        handleColumnClick(ev, column, col.onColumnClick),
                    columnActionsMode: col.columnActionsMode ?? hasDropdown ? ColumnActionsMode.hasDropdown : undefined
                };
            }),
        [columns]
    );

    React.useEffect(() => setTableKey(getId()), [isAccessibleMode]);

    // Deletes the selected rows using keyboard delete button.
    React.useEffect(() => {
        const handler = (ev: KeyboardEvent) => {
            if (ev.key === LuisKeyCodes.Delete && selection.count > 0) {
                onDeleteKeyUp();
            }
        };
        window.addEventListener('keyup', handler);

        return () => window.removeEventListener('keyup', handler);
    });

    const sortedColumn = adjustedColumns.find(c => c.isSorted);

    React.useEffect(() => {
        if (!sortedColumn) {
            return;
        }
        const sortedAriaMessage = sortedColumn.isSortedDescending
            ? localize(messages.sortDescendingAriaLabel)
            : localize(messages.sortAscendingAriaLabel);

        announceAction(`${sortedColumn.name} ${sortedAriaMessage}`);
    }, [sortedColumn]);

    const isMultiSelect = selectionMode === SelectionMode.multiple;

    const resizeDialogContentProps = {
        type: DialogType.normal,
        title: localize(messages.resizeColumnDialogTitle),
        closeButtonAriaLabel: localize(messages.cancel),
        subText: localize(messages.columnWidth)
    };

    const resizeColumn = (column: TableColumnProps) => {
        columnToEditRef.current = column;
        setIsResizeDialogHidden(false);
    };

    const sortColumn = (column: TableColumnProps) => {
        columnToEditRef.current = column;
        onSortColumn?.(column);
    };

    const getContextualMenuProps = (ev: React.MouseEvent<HTMLElement>, column: TableColumnProps): IContextualMenuProps => {
        const contextualMenuItems = [];

        if (column.isResizable) {
            contextualMenuItems.push({ key: 'resize', text: localize(messages.resize), onClick: () => resizeColumn(column) });
        }

        if (column.isSortable) {
            contextualMenuItems.push({
                key: 'sort',
                text: column.isSortedDescending ? localize(messages.sortAscending) : localize(messages.sortDescending),
                onClick: () => sortColumn(column)
            });
        }

        return contextualMenuItems.length
            ? {
                  items: contextualMenuItems,
                  gapSpace: 10,
                  isBeakVisible: true,
                  target: ev.currentTarget as HTMLElement,
                  directionalHint: DirectionalHint.bottomLeftEdge,
                  onDismiss: () => setContextualMenuProps(undefined)
              }
            : null;
    };

    const handleResizeColumn = () => {
        const detailsList = detailsListRef.current;
        if (columnToEditRef.current && resizeDialogTextfieldRef.current && detailsList) {
            const width = Number(resizeDialogTextfieldRef.current.value);
            detailsList.updateColumn(columnToEditRef.current, { width });
        }
        resizeDialogTextfieldRef.current = null;
        setIsResizeDialogHidden(true);
    };

    return (
        <TableContainer>
            <SearchResultsAnnouncer isActive={areItemsFiltered} itemsLength={overallItemsCount} />

            <ShimmeredDetailsList
                setKey="set"
                items={items}
                key={tableKey}
                selection={selection}
                enableUpdateAnimations
                enableShimmer={isLoading}
                columns={adjustedColumns}
                componentRef={detailsListRef}
                groups={!isLoading && groups}
                selectionMode={selectionMode}
                checkboxVisibility={checkboxVisibility}
                checkboxCellClassName={centralizedCheckbox}
                layoutMode={DetailsListLayoutMode.justified}
                checkButtonAriaLabel={localize(messages.checkButtonAriaLabel)}
                detailsListStyles={respectViewportHeight ? listStyles : undefined}
                ariaLabelForSelectAllCheckbox={isMultiSelect ? localize(messages.selectAllAriaLabel) : undefined}
                ariaLabelForSelectionColumn={isMultiSelect ? localize(messages.selectColumnAriaLabel) : undefined}
                onItemInvoked={(_, idx) => selection?.setIndexSelected(idx, !selection.isIndexSelected(idx), false)}
                onRenderRow={(_rowProps, _rowDefaultRenderer) =>
                    onRenderRow?.(_rowProps, _rowDefaultRenderer) || defaultOnRenderRow(_rowProps, selectionPredicate)
                }
                {...rest}
            />
            {contextualMenuProps && <ContextualMenu {...contextualMenuProps} />}

            {!items.length &&
                !isLoading &&
                !groups?.length &&
                (areItemsFiltered ? (
                    <>
                        <EmptyPlaceholder block variant="mediumPlus">
                            {localize(messages.noSearchResults)}
                        </EmptyPlaceholder>

                        <Announced message={localize(messages.noSearchResults)} />
                    </>
                ) : (
                    <>
                        <EmptyPlaceholder block variant="mediumPlus">
                            {emptyPlaceholder}
                        </EmptyPlaceholder>

                        <Announced message={announcedMessage ?? (typeof emptyPlaceholder === 'string' ? emptyPlaceholder : '')} />
                    </>
                ))}

            {sortAnnouncer}

            {selection ? <Announced message={`${selection.count} ${localize(messages.narratorSelectedItems)}`} /> : null}

            <Dialog
                hidden={isResizeDialogHidden}
                dialogContentProps={resizeDialogContentProps}
                onDismiss={() => setIsResizeDialogHidden(true)}
                modalProps={{
                    isBlocking: false,
                    titleAriaId: 'Dialog',
                    subtitleAriaId: 'DialogSub',
                    styles: { main: { maxWidth: 450 } }
                }}>
                <TextField type="number" componentRef={resizeDialogTextfieldRef} ariaLabel={localize(messages.columnWidth)} />
                <DialogFooter>
                    <PrimaryButton onClick={handleResizeColumn} text={localize(messages.resize)} />
                    <DefaultButton onClick={() => setIsResizeDialogHidden(true)} text={localize(messages.cancel)} />
                </DialogFooter>
            </Dialog>
        </TableContainer>
    );
};
